import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import { useSnackbar } from '@components/SnackbarNotification';
import { useLoading } from '@contexts/LoadingContext';
import { getFriendlyErrorMessage } from '@utils/getFriendlyErrorMessage';
import { ROUTES, MESSAGES } from '@/constants';

type LoginFieldErrors = {
  email: string;
  password: string;
};

export const useLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFACode, setTwoFACode] = useState('');
  const [fieldErrors, setFieldErrors] = useState<LoginFieldErrors>({
    email: '',
    password: '',
  });

  const { login, verify2FA } = useAuth();
  const { showSnackbar } = useSnackbar();
  const { withLoading, isLoading } = useLoading();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectParam = searchParams.get('redirect');
  const redirectTo = redirectParam && redirectParam.trim() ? redirectParam : null;

  const validate = (): boolean => {
    const errors: LoginFieldErrors = { email: '', password: '' };
    let isValid = true;

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      errors.email = 'Email é obrigatório';
      isValid = false;
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(trimmedEmail)) {
        errors.email = 'Digite um email válido';
        isValid = false;
      }
    }

    const trimmedPassword = password.trim();
    if (!trimmedPassword) {
      errors.password = 'Senha é obrigatória';
      isValid = false;
    }

    setFieldErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      await withLoading(async () => {
        const result = await login(email, password);
        
        if (result.success) {
          if (result.requires2FA) {
            setRequires2FA(true);
            showSnackbar('Código de verificação enviado por email', 'success');
          } else if (result.requiresPayment === true) {
            const msg =
              result.requiresPaymentReason === 'payment_overdue'
                ? 'Acesso bloqueado por inadimplência. Vamos te levar para a página de pagamento para regularizar.'
                : result.requiresPaymentReason === 'billing_invoice_required'
                  ? 'É necessário emitir ou quitar a cobrança do período. Vamos te levar para a página de pagamento.'
                  : 'Seu período de teste expirou. Vamos te levar para a página de pagamento.';
            showSnackbar(msg, 'info');
            navigate(ROUTES.PAYMENT);
          } else {
            showSnackbar(MESSAGES.SUCCESS.LOGIN || 'Login realizado com sucesso!', 'success');
            navigate(redirectTo || ROUTES.APP);
          }
        } else {
          const errorMessage = 'Email ou senha inválidos';
          setFieldErrors({ email: '', password: errorMessage });
          showSnackbar(errorMessage, 'error');
        }
      }, 'Entrando...');
    } catch (error: unknown) {
      const err = error as { response?: { status?: number }; isNetworkError?: boolean };
      let errorMessage = 'Email ou senha inválidos';
      if (err.response?.status === 403) {
        errorMessage = getFriendlyErrorMessage(error, 'Você não tem permissão para esta ação.');
      } else if (err.response?.status === 401) {
        // 401 no login = credenciais inválidas (não é "sessão expirada")
        errorMessage = 'Usuário ou senha inválido.';
      } else if (err.isNetworkError) {
        errorMessage = getFriendlyErrorMessage(error, MESSAGES.ERRORS.NETWORK);
      } else {
        errorMessage = getFriendlyErrorMessage(error, 'Erro ao fazer login. Verifique sua conexão e tente novamente.');
      }
      setFieldErrors({ email: '', password: errorMessage });
      showSnackbar(errorMessage, 'error');
    }
  };

  const toggleShowPassword = () => {
    setShowPassword(prev => !prev);
  };

  const handleVerify2FA = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!twoFACode || twoFACode.length !== 6) {
      setFieldErrors({ email: '', password: 'Código deve ter 6 dígitos' });
      showSnackbar('Por favor, informe o código de 6 dígitos recebido por email', 'error');
      return;
    }

    try {
      await withLoading(async () => {
        const shouldNavigateToApp = await verify2FA(email, twoFACode);
        if (shouldNavigateToApp) {
          showSnackbar('Login realizado com sucesso!', 'success');
          navigate(redirectTo || ROUTES.APP);
        } else {
          showSnackbar('Login realizado com sucesso!', 'success');
        }
      }, 'Verificando código...');
    } catch (error: unknown) {
      const errorMessage = getFriendlyErrorMessage(error, 'Código 2FA inválido ou expirado');
      setFieldErrors({ email: '', password: errorMessage });
      showSnackbar(errorMessage, 'error');
      setTwoFACode(''); // Limpar código para tentar novamente
    }
  };

  return {
    email,
    password,
    showPassword,
    isLoading,
    fieldErrors,
    requires2FA,
    twoFACode,
    setEmail,
    setPassword,
    setTwoFACode,
    toggleShowPassword,
    handleSubmit,
    handleVerify2FA,
  };
};


